import { Injectable } from '@angular/core';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { DateTime, DurationObjectUnits, DurationUnits } from 'luxon';
import { Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { DatesService } from 'shared/services/dates/dates.service';


export interface DurationWithPercent extends DurationObjectUnits {
  percent?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FinalCountDownService {

  private readonly DATE_FORMAT: string = 'yyyy-LL-dd\'T\'HH:mm';
  private readonly DATE_FORMAT_UTC: string = 'yyyy-LL-dd\'T\'HH:mm:ss\'Z';

  constructor(
    private readonly contextService: ContextService,
    private readonly datesService: DatesService
  ) {}

  public getCurrentDate$(timeTravelTo?: string): Observable<DateTime> {
    return this.getCountryCode$.pipe(
      switchMap((countryCode) => this.datesService.getCurrentDate(countryCode, timeTravelTo))
    );
  }

  public getTimeToCountdown$(
    currentDate: DateTime,
    isLocalDate: boolean,
    startDate: string,
    endDate: string,
    units: DurationUnits,
    promotionEvent: boolean = false
  ): Observable<DurationWithPercent | undefined> {
    return this.getCountryCode$.pipe(
      map((countryCode: string) => {
        const timezone = isLocalDate ? this.datesService.getTimeZoneFromCountry(countryCode) : 'utc';
        const dateFormat = promotionEvent ? this.DATE_FORMAT_UTC : this.DATE_FORMAT;
        const startDateTime = promotionEvent
          ? currentDate
          : DateTime.fromFormat(startDate, dateFormat, { zone: timezone }).toUTC();
        const endDateTime = DateTime.fromFormat(endDate, dateFormat, { zone: timezone }).toUTC();

        if (currentDate > endDateTime) {
          return undefined;
        }

        if (startDateTime > currentDate) {
          return { days: 0, hours: 0, minutes: 0, seconds: 0, percent: '0%' };
        }

        const timeToCountdown: DurationWithPercent = endDateTime.diff(currentDate, units).toObject();
        timeToCountdown.percent = `${Math.round((100 * currentDate.diff(startDateTime, ['milliseconds']).toObject().milliseconds!)
          / endDateTime.diff(startDateTime, ['milliseconds']).toObject().milliseconds!).toString()}%`;

        return timeToCountdown;
      })
    );
  }

  private get getCountryCode$(): Observable<string> {
    return this.contextService.getContext$().pipe(
      first(),
      map((context) => context.store.countryCode)
    );
  }

}
